// WHICH RIG drives a bundle's branches. Resolved HERE, before anything is
// downloaded, because the answer decides what gets downloaded — by the time a
// branch is built its bytes are already paid for.
//
// THE DISTILLED `rig2mesh` ONNX IS THE DEFAULT, for now. The exact MetaHuman rig
// (OpenRigLogic + this character's own DNA, one `orl_pack.bin`) is opt-in behind
// `?rig=orl` while it is proven on real characters — so merging this changes what
// nobody asked to change, and every character keeps the rig it has today.
//
// `auto` still reaches for the exact rig in the one case where the alternative is
// nothing: a bundle that declares no rig2mesh this app can load. That is a state
// the exact rig makes normal (dropping the distillation is ~74 MB per branch), and
// a frozen head is not a better default than a working one.
//
// Only one rig is ever fetched, so neither costs the other anything.
//
// Pure, so the table below is unit-tested without a network or a bundle.

import type { RigMode } from "./ogsGpuFlags.ts";

export type ResolvedRig = "orl" | "rig2mesh" | "none";

/** What the person looking at a frozen head should DO — the pill's headline,
 *  where `why` is its detail line. Telling someone to upload a DNA when they
 *  typed `?rig=orl` at a character that has none states the fact they just typed.
 *
 *  No "re-export the bundle" value: a DNA fixes that frozen head too and is the
 *  supported path, and the bundle's shortcoming still shows up in `why`. */
export type RigFix = "upload-dna" | "drop-flag";

export interface RigResolution {
  /** The rig to build branches from. `none` renders every branch at its neutral
   *  pose — a real outcome, reported to the UI, never a silent one. */
  readonly rig: ResolvedRig;
  /** Human-readable reason, for the log line and the pill's detail line. */
  readonly why: string;
  /** The action that fixes a `rig: 'none'`, for the pill's headline. Meaningless
   *  (and ignored) when a rig did resolve. */
  readonly fix: RigFix;
  /** Whether this bundle needs a rig at all. A single-region bundle's FUSED geom
   *  takes `rig_params` directly, so `none` is its normal, healthy state. */
  readonly needsRig: boolean;
  /** The rig2mesh files to fetch — empty unless `rig === 'rig2mesh'`. */
  readonly files: readonly string[];
  /** A branch whose EXACT rig fails to build may lazily fetch its own rig2mesh
   *  and use that instead (OGSBranch.init). True only when the fallback is both
   *  available and wanted:
   *   - `?rig=orl` sets it false. Forcing a rig and then quietly getting the
   *     other one back defeats the reason the value exists.
   *   - a bundle with no loadable rig2mesh sets it false: there is nothing to
   *     fetch, and asking would 404 on every load.
   *  Costs nothing when it never fires — the bytes are not fetched at boot. */
  readonly lazyFallback: boolean;
}

export interface RigModeInput {
  /** ?rig=auto|orl|rig2mesh. */
  readonly want: RigMode;
  /** This character ships a complete `orl_pack.bin`. */
  readonly hasPack: boolean;
  /** Branches declaring a LOADABLE rig2mesh whose row actually exists. Already
   *  filtered by the caller: a declared-but-not-uploaded file, or the HPBASIS1
   *  `rig2mesh_hp` this app has no loader for, is not a fallback. */
  readonly rig2mesh: readonly { name: string; file: string }[];
  /** This bundle has a rig→verts step at all (sceneNeedsRigDeform). */
  readonly needsRig: boolean;
  readonly slug?: string;
}

/** Resolve the rig for one bundle. Logs the decision — which rig posed the face
 *  is the first question asked of a wrong-looking one, and inferring it from
 *  byte counts in the network tab is not a debugging experience. */
export function resolveRigMode(input: RigModeInput): RigResolution {
  const r = decide(input);
  const where = input.slug ? `'${input.slug}'` : "bundle";
  console.info(`[ogs] ${where} rig: ${r.rig} — ${r.why}`);
  return r;
}

function decide(input: RigModeInput): RigResolution {
  const { want, hasPack, rig2mesh, needsRig } = input;
  // Deduped: branches can name the same file (this format already shares trunks
  // that way), and the loader's fetch list is a plain concat — a repeat would
  // register the download twice and skew the boot progress denominator.
  const files = [...new Set(rig2mesh.map((b) => b.file))];
  const base = { needsRig, lazyFallback: false, fix: "upload-dna" } as const;
  // AUTOMATIC fallback only for a bundle that needs a rig at all. A single-region
  // bundle's FUSED geom takes `rig_params` directly — its face animates with no
  // rig→verts step — yet `singleRegionScene()` still names a `rig2mesh.onnx` when
  // the row exists. Falling back on that would quietly re-add ~74 MB to exactly
  // the characters that don't need it, and the pill deliberately stays silent for
  // them (sceneNeedsRigDeform), so nothing would explain the download.
  // `?rig=rig2mesh` still forces it — that is the escape hatch if one ever does.
  const fallback = (why: string): RigResolution =>
    files.length
      ? { ...base, rig: "rig2mesh", why, files }
      // Forced or fallen back to a rig the bundle doesn't carry. The head stays
      // at neutral and the pill says which of the two is missing, because
      // "upload a DNA" and "re-export the bundle" are different fixes.
      // Nothing to fall back TO. The fix stays `upload-dna` — a DNA gets this
      // character the exact rig regardless of what its bundle declares — and the
      // bundle's own shortcoming rides along in `why`.
      : { ...base, rig: "none", why: `${why}, and this bundle declares no loadable rig2mesh`, files: [] };

  if (want === "orl") {
    // Deliberately does NOT fall back: this value exists to see what a
    // pack-less character looks like under the exact rig alone.
    return hasPack
      ? { ...base, rig: "orl", why: "?rig=orl", files: [] }
      : { ...base, rig: "none", fix: "drop-flag" as const,
          why: "?rig=orl, but this character ships no orl_pack.bin", files: [] };
  }
  if (want === "rig2mesh") {
    return fallback("?rig=rig2mesh (the exact rig is not built, and its pack is not fetched)");
  }

  // --- auto: rig2mesh first --------------------------------------------------
  // A bundle with no rig→verts step needs neither rig, and must not be given one
  // (or warned about): a fused geom takes `rig_params` straight in. Asked before
  // anything else, so a pack cannot drag a fused bundle onto a rig it never uses.
  if (!needsRig) {
    return { ...base, rig: "none", files: [],
      why: "this bundle's fused geom needs no rig" };
  }
  if (files.length) {
    return { ...base, rig: "rig2mesh", files,
      why: hasPack
        ? "rig2mesh is the default for now — ?rig=orl selects this character's exact MetaHuman rig"
        : "the bundle's distilled rig2mesh (this character ships no orl_pack.bin)" };
  }
  // No loadable rig2mesh. The pack is what stands between this character and a
  // frozen head, so take it even though the exact rig is not the default yet.
  if (hasPack) {
    return { ...base, rig: "orl", files: [],
      why: "this bundle declares no loadable rig2mesh — falling back to the exact MetaHuman rig" };
  }
  return { ...base, rig: "none", files: [],
    why: "no orl_pack.bin in this character’s /ogs bundle, and this bundle declares no loadable rig2mesh" };
}
